/**
 * Hook that converts a list of venues into a supercluster-indexed set of
 * cluster + point features for the current map viewport. Rebuilds the index
 * whenever the venue list reference changes; recomputes the visible features
 * whenever the bounds or zoom change. Returns an empty array until the map
 * reports its first `idle` event (`bounds === null`).
 *
 * Tuning:
 *   - `radius: 60` — generous cluster footprint so MENA-sized maps don't
 *     leave dozens of single pins overlapping.
 *   - `maxZoom: 16` — past zoom 16 every venue resolves to its own pin.
 *
 * The hook is pure JS — no DOM access — so the mobile MapView (Task 12) can
 * reuse the same logic with a thin RN wrapper.
 */

import { useMemo } from 'react';
import Supercluster from 'supercluster';
import type { SportName } from '@bravemove/design-tokens/icons';

/**
 * The map surface reads the minimum venue shape it needs from F10's
 * `GET /public/venues` response. The full canonical `VenueListItem` lives in
 * [`packages/types/src/venue.ts`](../../../../../types/src/venue.ts) — this
 * local interface is structurally compatible so consumers can pass the real
 * type without a runtime workspace dep on `@bravemove/types`.
 */
export type VenueTier = 'tier_1' | 'tier_2' | 'tier_3';

export type VenueListItem = {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly tier: VenueTier;
  readonly lat: string;
  readonly lng: string;
  readonly sports: ReadonlyArray<{
    readonly sportName: SportName;
    readonly isPrimary: boolean;
  }>;
  readonly [key: string]: unknown;
};

export type VenueClusterPointProps = { readonly venue: VenueListItem };
export type VenueClusterAccumulator = { readonly point_count: number };

export type ClusterFeature = Supercluster.PointFeature<VenueClusterPointProps>;
export type ClusterPoint = Supercluster.ClusterFeature<VenueClusterAccumulator>;

export type MapBounds = {
  readonly west: number;
  readonly south: number;
  readonly east: number;
  readonly north: number;
};

/**
 * Build a Supercluster index from the supplied venues and return the visible
 * cluster + single-point features for the current viewport bounds and zoom.
 * Venue rows whose `lat` / `lng` strings fail to parse are dropped silently.
 */
export function useMapClusters(
  venues: readonly VenueListItem[],
  bounds: MapBounds | null,
  zoom: number,
): Array<ClusterFeature | ClusterPoint> {
  const index = useMemo(() => {
    const sc = new Supercluster<VenueClusterPointProps, VenueClusterAccumulator>({
      radius: 60,
      maxZoom: 16,
    });
    const points: ClusterFeature[] = [];
    for (const v of venues) {
      const lat = Number.parseFloat(v.lat);
      const lng = Number.parseFloat(v.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      points.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: { venue: v },
      });
    }
    sc.load(points);
    return sc;
  }, [venues]);

  return useMemo(() => {
    if (!bounds) return [];
    return index.getClusters(
      [bounds.west, bounds.south, bounds.east, bounds.north],
      Math.round(zoom),
    );
  }, [index, bounds, zoom]);
}
